/**
 * ClusterDetailPanel — neurol-ux DetailPanel.tsx birebir adaptasyonu
 * (Top Pages ve Top Keywords gösterilmiyor — kullanıcı isteği)
 */
import { useState } from "react";
import { Sparkles, X, Crown, FileText, ChevronRight, Globe2, Layers } from "lucide-react";
import { useGetClusterPages } from "@workspace/api-client-react";

// ─── Neurol-UX renk tokenları ─────────────────────────────────────────────────
const C = {
  bg:       "#fafaf9",
  surface:  "#ffffff",
  elevated: "#f4f4f2",
  border:   "#e7e7e4",
  borderS:  "#e8edf3",
  text1:    "#1d1d1b",
  text2:    "#70706c",
  textM:    "#70706c",
  blue:     "#ec6461",
  purple:   "#e83e3c",
  green:    "#12b76a",
  cyan:     "#e83e3c",
  amber:    "#f79009",
  red:      "#f04438",
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────
export interface PanelCluster {
  id: number;
  name: string;
  parentId?: number | null;
  pageCount?: number;
  avgScore?: number | null;
  pillarPageTitle?: string | null;
  pillarPageUrl?: string | null;
  children?: PanelCluster[];
}

interface ClusterDetailPanelProps {
  cluster: PanelCluster;
  allClusters: PanelCluster[];
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getLangFromName(name: string): string | null {
  return name.match(/^\[([A-Z]{2})\] /)?.[1] ?? null;
}
function getDisplayName(name: string) {
  return name.replace(/^\[[A-Z]{2}\] /, "");
}
const LANG_ACCENT: Record<string, string> = {
  EN: C.blue, TR: C.purple, AR: C.green, DE: C.amber, FR: "#f18e8b",
};
const LANG_FLAG: Record<string, string> = {
  EN: "🇬🇧", TR: "🇹🇷", AR: "🇸🇦", DE: "🇩🇪", FR: "🇫🇷",
};
function getAccent(name: string) {
  const lang = getLangFromName(name);
  return lang ? (LANG_ACCENT[lang] ?? C.cyan) : C.cyan;
}
function buildAncestors(id: number, all: PanelCluster[]): PanelCluster[] {
  const byId = new Map(all.map((c) => [c.id, c]));
  const chain: PanelCluster[] = [];
  let cur = byId.get(id);
  const seen = new Set<number>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur);
    cur = cur.parentId != null ? byId.get(cur.parentId) : undefined;
  }
  return chain;
}

type Tab = "overview" | "pages" | "clusters";

// ─── Main panel ───────────────────────────────────────────────────────────────
export function ClusterDetailPanel({ cluster, allClusters, onClose }: ClusterDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const { data: pagesData } = useGetClusterPages(cluster.id);
  const pages = (pagesData ?? []) as Array<{
    id: number; url: string; title?: string | null;
    score?: number | null; isPillar?: boolean;
    incomingLinkCount?: number; outgoingLinkCount?: number;
  }>;

  const subClusters = cluster.children ?? [];
  const lang = getLangFromName(cluster.name);
  const accent = getAccent(cluster.name);
  const displayName = getDisplayName(cluster.name);
  const ancestors = buildAncestors(cluster.id, allClusters);

  // Authority score = avgScore ya da sabit 0–100 (neurol-ux gibi daire)
  const authority = Math.min(100, Math.max(0, Math.round((cluster.avgScore ?? 0) * 10)));
  const circumference = 2 * Math.PI * 32;
  const strokeDashoffset = circumference - (authority / 100) * circumference;

  const isRoot = !cluster.parentId;
  const typeLabel = isRoot ? "pillar" : subClusters.length > 0 ? "cluster" : "cluster";
  const typeBg = isRoot
    ? `${C.blue}18`
    : `${C.purple}18`;
  const typeColor = isRoot ? C.blue : C.purple;

  // inbound / outbound totals from pages
  const totalInbound  = pages.reduce((s, p) => s + (p.incomingLinkCount ?? 0), 0);
  const totalOutbound = pages.reduce((s, p) => s + (p.outgoingLinkCount ?? 0), 0);

  const tabs: { id: Tab; label: string }[] = [
    { id: "overview",  label: "Genel Bakış" },
    { id: "pages",     label: `Sayfalar (${cluster.pageCount ?? pages.length})` },
    { id: "clusters",  label: `Alt Cluster (${subClusters.length})` },
  ];

  return (
    <aside style={{
      width: 320,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      background: C.surface,
      borderLeft: `1px solid ${C.borderS}`,
      height: "100%",
      overflow: "hidden",
      fontFamily: "Inter, -apple-system, sans-serif",
    }}>
      {/* ── Header (neurol-ux DetailPanel header birebir) ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", borderBottom: `1px solid ${C.borderS}`, padding: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: C.text1, margin: 0, lineHeight: 1.2, wordBreak: "break-word", maxWidth: 230 }}>
              {displayName}
            </h2>
            <span style={{
              borderRadius: 20, padding: "2px 8px",
              fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em",
              background: typeBg, color: typeColor,
            }}>
              {typeLabel}
            </span>
          </div>
          {lang && (
            <span style={{ fontSize: 10, color: C.textM }}>
              {LANG_FLAG[lang] ?? "🌐"} {lang}
            </span>
          )}
        </div>
        <button onClick={onClose} style={{
          padding: 6, borderRadius: 8, border: "none",
          background: "transparent", color: C.textM, cursor: "pointer",
          flexShrink: 0,
        }}>
          <X size={15} />
        </button>
      </div>

      {/* ── Tabs (neurol-ux DetailPanel tabs birebir) ── */}
      <div style={{ display: "flex", borderBottom: `1px solid ${C.borderS}`, paddingLeft: 20, overflowX: "auto" }}>
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: "10px 12px",
            fontSize: 11,
            fontWeight: 500,
            color: activeTab === tab.id ? C.purple : C.textM,
            background: "transparent",
            border: "none",
            borderBottom: `2px solid ${activeTab === tab.id ? C.purple : "transparent"}`,
            cursor: "pointer",
            transition: "all 0.15s",
            whiteSpace: "nowrap",
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>

        {/* ── GENEL BAKIŞ TAB (neurol-ux overview birebir) ── */}
        {activeTab === "overview" && (
          <>
            {/* Metrics grid (4 kart: Total Pages, Sub Clusters, Gelen Link, Giden Link) */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
              {[
                { label: "Toplam Sayfa",  value: cluster.pageCount ?? pages.length },
                { label: "Alt Cluster",   value: subClusters.length },
                { label: "Gelen Link",    value: totalInbound },
                { label: "Giden Link",    value: totalOutbound },
              ].map(({ label, value }) => (
                <div key={label} style={{
                  borderRadius: 8,
                  border: `1px solid ${C.borderS}`,
                  background: C.elevated,
                  padding: "10px 12px",
                }}>
                  <p style={{ fontSize: 9, color: C.textM, margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</p>
                  <p style={{ fontSize: 18, fontWeight: 600, color: C.text1, margin: 0 }}>{value}</p>
                </div>
              ))}
            </div>

            {/* Authority Score gauge (neurol-ux birebir) */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 12,
              border: `1px solid ${C.borderS}`,
              background: C.elevated,
              padding: 20,
              marginBottom: 20,
            }}>
              <div style={{ position: "relative", width: 80, height: 80 }}>
                <svg style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }} viewBox="0 0 72 72">
                  <circle cx="36" cy="36" r="32" fill="none" stroke="#e8edf3" strokeWidth="5" />
                  <circle cx="36" cy="36" r="32" fill="none" stroke={C.purple}
                    strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} />
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 17, fontWeight: 700, color: C.text1 }}>{authority}</span>
                  <span style={{ fontSize: 8, color: C.textM }}>/100</span>
                </div>
              </div>
              <div style={{ marginLeft: 16 }}>
                <p style={{ fontSize: 11, fontWeight: 500, color: C.text2, margin: "0 0 2px" }}>Authority Score</p>
                <p style={{ fontSize: 9, color: C.textM, margin: 0 }}>Link equity'e göre</p>
              </div>
            </div>

            {/* Hiyerarşi (üst/alt cluster) */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: C.textM, margin: "0 0 10px" }}>
                ÜST / ALT CLUSTER HİYERARŞİSİ
              </h3>
              <div style={{ background: C.elevated, border: `1px solid ${C.borderS}`, borderRadius: 10, padding: 12 }}>
                {/* Site kökü */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 8, borderBottom: `1px solid ${C.borderS}` }}>
                  <Globe2 size={11} style={{ color: C.textM, flexShrink: 0 }} />
                  <span style={{ fontSize: 10, color: C.textM, fontWeight: 500 }}>SİTE KÖKÜ</span>
                </div>

                {/* Ancestor zinciri */}
                {ancestors.map((anc, i) => {
                  const isLast = i === ancestors.length - 1;
                  const ancAccent = getAccent(anc.name);
                  return (
                    <div key={anc.id} style={{
                      marginLeft: i * 10,
                      paddingTop: 8,
                      paddingBottom: isLast ? 0 : 8,
                      borderBottom: isLast ? "none" : `1px solid ${C.borderS}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}>
                      <ChevronRight size={9} style={{ color: C.textM, flexShrink: 0 }} />
                      <span style={{
                        fontSize: 11,
                        fontWeight: isLast ? 700 : 500,
                        color: isLast ? ancAccent : C.text2,
                        background: isLast ? `${ancAccent}18` : "transparent",
                        padding: isLast ? "2px 7px" : "0",
                        borderRadius: isLast ? 6 : 0,
                        border: isLast ? `1px solid ${ancAccent}30` : "none",
                      }}>
                        {getDisplayName(anc.name)}
                      </span>
                    </div>
                  );
                })}

                {/* Alt clusterlar */}
                {subClusters.length > 0 && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.borderS}` }}>
                    <div style={{ fontSize: 9, color: C.textM, marginBottom: 5 }}>Bu cluster'ın altında ({subClusters.length})</div>
                    {subClusters.slice(0, 3).map((sub) => (
                      <div key={sub.id} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "4px 8px", borderRadius: 5,
                        background: `${C.purple}10`, border: `1px solid ${C.purple}20`,
                        marginBottom: 4,
                      }}>
                        <span style={{ fontSize: 10, color: C.text2 }}>{getDisplayName(sub.name)}</span>
                        <span style={{ fontSize: 8, color: C.textM }}>Alt cluster · {sub.pageCount ?? 0} sayfa</span>
                      </div>
                    ))}
                    {subClusters.length > 3 && (
                      <span style={{ fontSize: 9, color: C.textM }}>+{subClusters.length - 3} daha...</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Pillar sayfa */}
            {cluster.pillarPageTitle && (
              <div>
                <h3 style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: C.textM, margin: "0 0 8px" }}>
                  ★ Pillar Sayfa (zorunlu)
                </h3>
                <a href={cluster.pillarPageUrl ?? "#"} target="_blank" rel="noopener noreferrer" style={{
                  display: "block", padding: "10px 14px",
                  background: `${C.amber}12`, border: `1px solid ${C.amber}40`,
                  borderRadius: 10, textDecoration: "none",
                  boxShadow: `0 0 12px rgba(245,158,11,0.12)`,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 2 }}>{cluster.pillarPageTitle}</div>
                  <div style={{ fontSize: 9, color: C.textM }}>Cluster hub</div>
                </a>
              </div>
            )}
          </>
        )}

        {/* ── SAYFALAR TAB ── */}
        {activeTab === "pages" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {pages.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: C.textM, fontSize: 12 }}>Henüz sayfa yok</div>
            ) : (
              pages.map((p) => (
                <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px",
                  background: C.elevated,
                  border: `1px solid ${p.isPillar ? `${C.amber}50` : C.borderS}`,
                  borderRadius: 8, textDecoration: "none",
                }}>
                  {p.isPillar
                    ? <Crown size={11} style={{ color: C.amber, flexShrink: 0 }} />
                    : <FileText size={11} style={{ color: C.textM, flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: C.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.title || p.url}
                    </div>
                    <div style={{ fontSize: 9, color: C.textM, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.url}
                    </div>
                  </div>
                  {typeof p.score === "number" && (
                    <span style={{ fontSize: 9, color: accent, background: `${accent}18`, padding: "1px 5px", borderRadius: 4, flexShrink: 0 }}>
                      {p.score}
                    </span>
                  )}
                </a>
              ))
            )}
          </div>
        )}

        {/* ── ALT CLUSTER TAB ── */}
        {activeTab === "clusters" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {subClusters.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: C.textM, fontSize: 12 }}>Alt cluster yok</div>
            ) : (
              subClusters.map((sub) => (
                <div key={sub.id} style={{
                  padding: "10px 12px",
                  background: C.elevated,
                  border: `1px solid ${C.borderS}`,
                  borderRadius: 10,
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <Layers size={13} style={{ color: getAccent(sub.name), flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text1 }}>{getDisplayName(sub.name)}</div>
                    <div style={{ fontSize: 9, color: C.textM }}>{sub.pageCount ?? 0} sayfa · {sub.children?.length ?? 0} alt cluster</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* ── AI Suggestions footer (neurol-ux birebir) ── */}
      <div style={{ borderTop: `1px solid ${C.borderS}`, padding: 16 }}>
        <button style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          borderRadius: 12, background: `${C.purple}18`, border: "none",
          padding: "10px 0", fontSize: 13, fontWeight: 500, color: C.purple, cursor: "pointer",
          transition: "background 0.15s",
        }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = `${C.purple}28`; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = `${C.purple}18`; }}
        >
          <Sparkles size={15} />
          AI Suggestions
        </button>
      </div>
    </aside>
  );
}
